import React, { useEffect, useState, useMemo } from 'react'
import {
  Card,
  Space,
  Typography,
  Switch,
  Button,
  Divider,
  Row,
  Col,
  Alert,
  Spin,
  Badge,
} from 'antd'
import { BiBell, BiShield, BiRadar, BiInfoCircle } from 'react-icons/bi'
import { useSelector } from 'react-redux'
import LocationPreferenceToggle, { LocationPreferenceValue } from '../../../components/LocationPreferenceToggle'
import LocationPinSelector from '../../../components/LocationPinSelector'
import RadiusSelector from '../../../components/RadiusSelector'
import { RootState } from '../../../app/store'
import { useAppDispatch } from '../../../app/hooks'
import {
  getVapidKey,
  getUserSubscriptions,
  createSubscription,
  updateSubscriptionPreferences,
  deleteSubscription,
} from './actions'
import WebPushService from '../../../utils/webPushService'
import {
  NotificationPreferencesT,
  NearbyIncidentAlertsT,
  NearbyIncidentAlertsPostcodeT,
  NearbyIncidentAlertsRadiusT,
  NearbyIncidentAlertsAllT,
} from '../../../types/notificationSubscriptionT'
import StatusE from '../../../types/statusE'
import { UserT } from '../../../types/userT'
import Token from '../../../token'

const getDefaultPreferences = (): NotificationPreferencesT => ({
  enabled: true,
  nearby_incident_alerts: {
    enabled: true,
    location_type: 'postcode',
  },
})

interface NearbyIncidentAlertsData {
  postcode?: string
  latitude?: number
  longitude?: number
  radius?: number
}

const buildNearbyIncidentAlerts = (
  locationType: LocationPreferenceValue,
  enabled: boolean,
  data: NearbyIncidentAlertsData = {}
): NearbyIncidentAlertsT => {
  if (locationType === 'all') {
    return {
      enabled,
      location_type: 'all',
    } as NearbyIncidentAlertsAllT
  }

  if (locationType === 'radius') {
    return {
      enabled,
      location_type: 'radius',
      latitude: data.latitude ?? 0,
      longitude: data.longitude ?? 0,
      radius: data.radius ?? 5,
    }
  }

  const result: NearbyIncidentAlertsPostcodeT = {
    enabled,
    location_type: 'postcode',
  }

  if (data.postcode) {
    result.postcode = data.postcode
  }

  return result
}

export default function NotificationSettings() {
  const dispatch = useAppDispatch()
  const {
    status,
    error,
    subscriptions,
    vapidPublicKey,
    isSubscribed,
    isLoadingVapidKey,
    isLoadingSubscriptions,
  } = useSelector((state: RootState) => state.notifications)
  const user = useSelector((state: RootState) => state.account.user) as UserT | undefined

  const userPostcode = useMemo(() => {
    if (!user?.address) return undefined
    return user.address['mepr-address-zip'] || undefined
  }, [user])

  const userFormattedAddress = useMemo(() => {
    if (!user?.address) return undefined
    const parts = [
      user.address['mepr-address-one'],
      user.address['mepr-address-two'],
      user.address['mepr-address-city'],
      user.address['mepr-address-state'],
      user.address['mepr-address-zip'],
      user.address['mepr-address-country'],
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : undefined
  }, [user])

  const [isEnabling, setIsEnabling] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreferencesT>(getDefaultPreferences())
  const [browserSupported, setBrowserSupported] = useState(false)
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default')

  useEffect(() => {
    setBrowserSupported(WebPushService.isSupported())

    if (WebPushService.isSupported()) {
      setPermissionStatus(Notification.permission)
    }

    dispatch(getVapidKey())

    const loadSubscriptions = async () => {
      await Token.ensureValidToken()
      dispatch(getUserSubscriptions())
    }
    loadSubscriptions()
  }, [dispatch])

  useEffect(() => {
    if (vapidPublicKey) {
      WebPushService.setVapidKey(vapidPublicKey)
    }
  }, [vapidPublicKey])

  useEffect(() => {
    if (subscriptions.length > 0) {
      const currentSub = subscriptions[0]
      const nearbyAlerts = currentSub.preferences.nearby_incident_alerts
      const normalizedNearbyAlerts = buildNearbyIncidentAlerts(
        nearbyAlerts.location_type,
        nearbyAlerts.enabled,
        nearbyAlerts as NearbyIncidentAlertsData
      )
      setPreferences({
        enabled: currentSub.preferences.enabled,
        nearby_incident_alerts: normalizedNearbyAlerts,
      })
    }
  }, [subscriptions])

  const handleEnableNotifications = async () => {
    if (!browserSupported) {
      return
    }

    setIsEnabling(true)

    try {
      const pushSubscription = await WebPushService.subscribeToPush()

      if (pushSubscription) {
        const subscriptionData = WebPushService.subscriptionToApiFormat(
          pushSubscription,
          preferences
        )

        dispatch(createSubscription(subscriptionData))
        setPermissionStatus('granted')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred while enabling notifications'

      dispatch({
        type: 'notifications/setError',
        payload: {
          status: 400,
          name: 'NotificationError',
          message: errorMessage
        }
      })
    } finally {
      setIsEnabling(false)
    }
  }

  const handleDisableNotifications = async () => {
    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(deleteSubscription(subscription.id))

      try {
        await WebPushService.unsubscribeFromPush()
      } catch (err) {
        // Silently handle unsubscribe failure - the subscription was already removed from the backend
      }
    }
  }

  const handleGlobalEnabledChange = (enabled: boolean) => {
    const newPreferences: NotificationPreferencesT = { ...preferences, enabled }
    setPreferences(newPreferences)

    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(updateSubscriptionPreferences(subscription.id, {
        preferences: newPreferences
      }))
    }
  }

  const handleNearbyAlertsEnabledChange = (enabled: boolean) => {
    const currentAlerts = preferences.nearby_incident_alerts
    const newNearbyIncidentAlerts = buildNearbyIncidentAlerts(
      currentAlerts.location_type,
      enabled,
      currentAlerts as NearbyIncidentAlertsData
    )

    const newPreferences: NotificationPreferencesT = {
      ...preferences,
      nearby_incident_alerts: newNearbyIncidentAlerts,
    }
    setPreferences(newPreferences)

    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(updateSubscriptionPreferences(subscription.id, {
        preferences: newPreferences
      }))
    }
  }

  const handleAlertMethodChange = (method: LocationPreferenceValue) => {
    const currentEnabled = preferences.nearby_incident_alerts.enabled
    const newNearbyIncidentAlerts = buildNearbyIncidentAlerts(method, currentEnabled)

    const newPreferences: NotificationPreferencesT = {
      ...preferences,
      nearby_incident_alerts: newNearbyIncidentAlerts,
    }
    setPreferences(newPreferences)

    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(updateSubscriptionPreferences(subscription.id, {
        preferences: newPreferences
      }))
    }
  }

  const handleLocationChange = (coordinates: { latitude: number; longitude: number }) => {
    if (preferences.nearby_incident_alerts.location_type !== 'radius') {
      return
    }

    const currentAlerts = preferences.nearby_incident_alerts as NearbyIncidentAlertsRadiusT
    const newNearbyIncidentAlerts = buildNearbyIncidentAlerts('radius', currentAlerts.enabled, {
      ...currentAlerts,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    })

    const newPreferences: NotificationPreferencesT = {
      ...preferences,
      nearby_incident_alerts: newNearbyIncidentAlerts,
    }
    setPreferences(newPreferences)

    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(updateSubscriptionPreferences(subscription.id, {
        preferences: newPreferences
      }))
    }
  }

  const handleRadiusChange = (radius: number) => {
    if (preferences.nearby_incident_alerts.location_type !== 'radius') {
      return
    }

    const currentAlerts = preferences.nearby_incident_alerts as NearbyIncidentAlertsRadiusT
    const newNearbyIncidentAlerts = buildNearbyIncidentAlerts('radius', currentAlerts.enabled, {
      ...currentAlerts,
      radius,
    })

    const newPreferences: NotificationPreferencesT = {
      ...preferences,
      nearby_incident_alerts: newNearbyIncidentAlerts,
    }
    setPreferences(newPreferences)

    if (subscriptions.length > 0) {
      const subscription = subscriptions[0]
      dispatch(updateSubscriptionPreferences(subscription.id, {
        preferences: newPreferences
      }))
    }
  }

  const getLocationType = (): LocationPreferenceValue =>
    preferences.nearby_incident_alerts.location_type

  const getCurrentRadius = (): number => {
    if (preferences.nearby_incident_alerts.location_type === 'radius') {
      return preferences.nearby_incident_alerts.radius
    }
    return 5
  }

  const getCurrentCoordinates = (): { latitude: number; longitude: number } | undefined => {
    if (preferences.nearby_incident_alerts.location_type === 'radius') {
      const { latitude, longitude } = preferences.nearby_incident_alerts
      if (latitude !== undefined && longitude !== undefined) {
        return { latitude, longitude }
      }
    }
    return undefined
  }

  const renderNotificationStatus = () => {
    if (!browserSupported) {
      return (
        <Alert
          message="Browser Not Supported"
          description="Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Safari."
          type="warning"
          showIcon
        />
      )
    }

    if (permissionStatus === 'denied') {
      return (
        <Alert
          message="Notifications Blocked"
          description="Notifications are blocked in your browser. Please enable them in your browser settings to receive alerts."
          type="error"
          showIcon
        />
      )
    }

    if (isSubscribed) {
      return (
        <Alert
          message="Notifications Enabled"
          description="You're successfully subscribed to receive security incident notifications."
          type="success"
          showIcon
        />
      )
    }

    return (
      <Alert
        message="Notifications Disabled"
        description="Enable notifications to receive real-time security alerts in your area."
        type="info"
        showIcon
      />
    )
  }

  const isLoading = (isLoadingVapidKey || isLoadingSubscriptions) || status === StatusE.LOADING || isEnabling

  return (
    <Card
      title={
        <Space>
          <BiBell />
          <Typography.Text strong>Notification Preferences</Typography.Text>
          {isSubscribed && <Badge status="success" />}
        </Space>
      }
      style={{
        opacity: (isLoadingVapidKey || isLoadingSubscriptions) ? 0.6 : 1,
        transition: 'opacity 0.3s ease',
      }}
    >
      <Spin spinning={isLoading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {renderNotificationStatus()}

          {error && (
            <Alert
              message="Error"
              description={error.message || 'An error occurred'}
              type="error"
              showIcon
              closable
            />
          )}

          <Row justify="space-between" align="middle">
            <Col>
              <Typography.Text strong>Push Notifications</Typography.Text>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                Receive real-time security alerts directly in your browser
              </Typography.Text>
            </Col>
            <Col>
              {isSubscribed ? (
                <Button
                  danger
                  onClick={handleDisableNotifications}
                  disabled={isLoading}
                >
                  Disable Notifications
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleEnableNotifications}
                  disabled={!browserSupported || permissionStatus === 'denied' || isLoading}
                  loading={isEnabling}
                >
                  Enable Notifications
                </Button>
              )}
            </Col>
          </Row>

          {isSubscribed && (
            <>
              <Divider />

              <Row justify="space-between" align="middle">
                <Col flex={1}>
                  <Space>
                    <BiShield />
                    <div>
                      <Typography.Text strong>Platform Alerts</Typography.Text>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        General security notifications for all platform users
                      </Typography.Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Switch
                    checked={preferences.nearby_incident_alerts.enabled}
                    onChange={handleNearbyAlertsEnabledChange}
                    disabled={isLoading}
                  />
                </Col>
              </Row>

              <Row justify="space-between" align="middle">
                <Col flex={1}>
                  <Space>
                    <BiRadar />
                    <div>
                      <Typography.Text strong>Geographic Alerts</Typography.Text>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        Receive location-based incident alerts
                      </Typography.Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Switch
                    checked={preferences.enabled}
                    onChange={handleGlobalEnabledChange}
                    disabled={isLoading}
                  />
                </Col>
              </Row>

              {preferences.enabled && (
                <>
                  <Divider />

                  <LocationPreferenceToggle
                    value={getLocationType()}
                    onChange={handleAlertMethodChange}
                    disabled={isLoading}
                    postcodeArea={userPostcode}
                  />

                  {getLocationType() === 'radius' && (
                    <>
                      <LocationPinSelector
                        initialCoordinates={getCurrentCoordinates()}
                        accommodationAddress={userFormattedAddress}
                        onLocationChange={handleLocationChange}
                        disabled={isLoading}
                        height={300}
                        radius={getCurrentRadius()}
                      />

                      <RadiusSelector
                        value={getCurrentRadius()}
                        onChange={handleRadiusChange}
                        disabled={isLoading}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}

          <Typography.Paragraph type="secondary" style={{ fontSize: '12px', margin: 0 }}>
            <BiInfoCircle />&nbsp;Platform Alerts notify all users about general security incidents across the platform. Geographic Alerts are location-specific and can be based on your registered postal code area, a customizable radius around your chosen map location, or set to receive all incidents regardless of location. All notifications respect your privacy and only contain relevant security information.
          </Typography.Paragraph>
        </Space>
      </Spin>
    </Card>
  )
}
